// Unit conversion utilities

export type UnitSystem = "imperial" | "metric"
export type Currency = "USD" | "EUR" | "GBP"
export type TemperatureUnit = "celsius" | "fahrenheit"

export interface UserPreferences {
  unitSystem: UnitSystem
  currency: Currency
  temperatureUnit: TemperatureUnit
}

// Wave height conversions
export function convertWaveHeight(value: string, toSystem: UnitSystem): string {
  // Handle range formats like "6-8 ft" or "1.8-2.4 m"
  const rangeMatch = value.match(/(\d+\.?\d*)\s*-\s*(\d+\.?\d*)\s*(ft|m)/i)
  if (rangeMatch) {
    const [, min, max, unit] = rangeMatch
    const minNum = Number.parseFloat(min)
    const maxNum = Number.parseFloat(max)
    const currentUnit = unit.toLowerCase()

    if (toSystem === "metric") {
      if (currentUnit === "ft") {
        // Convert feet to meters
        const minM = (minNum * 0.3048).toFixed(1)
        const maxM = (maxNum * 0.3048).toFixed(1)
        return `${minM}-${maxM} m`
      }
      return value // Already in meters
    } else {
      if (currentUnit === "m") {
        // Convert meters to feet
        const minFt = Math.round(minNum * 3.28084)
        const maxFt = Math.round(maxNum * 3.28084)
        return `${minFt}-${maxFt} ft`
      }
      return value // Already in feet
    }
  }

  // Handle single values like "6 ft" or "1.8 m"
  const singleMatch = value.match(/(\d+\.?\d*)\s*(ft|m)/i)
  if (singleMatch) {
    const [, num, unit] = singleMatch
    const numValue = Number.parseFloat(num)
    const currentUnit = unit.toLowerCase()

    if (toSystem === "metric") {
      if (currentUnit === "ft") {
        const meters = (numValue * 0.3048).toFixed(1)
        return `${meters} m`
      }
      return value
    } else {
      if (currentUnit === "m") {
        const feet = Math.round(numValue * 3.28084)
        return `${feet} ft`
      }
      return value
    }
  }

  return value
}

// Wind speed conversions
export function convertWindSpeed(value: string, toSystem: UnitSystem): string {
  const match = value.match(/(\d+\.?\d*)\s*(mph|km\/h|kmh|kph)/i)
  if (!match) return value

  const [, num, unit] = match
  const numValue = Number.parseFloat(num)
  const currentUnit = unit.toLowerCase().replace(/\//g, "")

  if (toSystem === "metric") {
    if (currentUnit === "mph") {
      const kmh = Math.round(numValue * 1.60934)
      return `${kmh} km/h`
    }
    return value // Already in km/h
  } else {
    if (currentUnit === "kmh" || currentUnit === "kph") {
      const mph = Math.round(numValue * 0.621371)
      return `${mph} mph`
    }
    return value // Already in mph
  }
}

// Temperature conversions (for future use)
export function convertTemperature(value: number, fromUnit: TemperatureUnit, toUnit: TemperatureUnit): string {
  if (fromUnit === toUnit) {
    return `${Math.round(value)}°${toUnit === "celsius" ? "C" : "F"}`
  }

  if (toUnit === "celsius") {
    // Convert Fahrenheit to Celsius
    const celsius = ((value - 32) * 5) / 9
    return `${Math.round(celsius)}°C`
  } else {
    // Convert Celsius to Fahrenheit
    const fahrenheit = (value * 9) / 5 + 32
    return `${Math.round(fahrenheit)}°F`
  }
}

// Helper to convert temperature string (e.g., "72°F")
export function convertTemperatureString(tempString: string, toUnit: TemperatureUnit): string {
  const fahrenheitMatch = tempString.match(/(\d+\.?\d*)°?F/i)
  const celsiusMatch = tempString.match(/(\d+\.?\d*)°?C/i)

  if (fahrenheitMatch) {
    const value = Number.parseFloat(fahrenheitMatch[1])
    if (isNaN(value)) return tempString
    return convertTemperature(value, "fahrenheit", toUnit)
  } else if (celsiusMatch) {
    const value = Number.parseFloat(celsiusMatch[1])
    if (isNaN(value)) return tempString
    return convertTemperature(value, "celsius", toUnit)
  }

  return tempString
}
